import * as React from "react";

import {bindInput}    from "@Common/react/bind-input";
import {scopedState}  from "@Common/react/scoped-state";

import {FloatingInput} from "@Xpresso/routes/account/common/floating-labels/floating-input";

import {PAGE_VIEW} from "./xpresslinks-landing";

export const contactFormStateValidator = ({state, includeAddressFields}):boolean => {
    const {firstName, lastName, email, phone, address, zip} = state.value;
    //validate email format
    if(email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return false;
    }
    const addressFieldsRequired = includeAddressFields ? (address && zip) : true
    return firstName && lastName && email && phone && addressFieldsRequired;
}

export const contactFormStateValidatorMessage = ({state, includeAddressFields}):string => {
    return contactFormStateValidator({state, includeAddressFields}) ? "" : "Please fill in all required fields";
}

export const contactFormContent = ({state, showError, includeAddressFields, view=null}) => {
    const enabled = view?.value !== PAGE_VIEW.EDIT

    return (
        <div className="form">
            <FloatingInput
                classContainer="input"
                classLabel={`label ${!enabled ? 'disabled' : ''}`}
                props={{
                    ...bindInput({
                        value: scopedState(state, ["firstName"]).value,
                        onChange: (value) => {
                            scopedState(state, ["firstName"]).onChange(value);
                            showError.onChange(false);
                        }
                    }),
                    autoComplete: "given-name",
                    className: "value",
                    disabled: !enabled,
                    "xd-qa-type":"textbox",
                    "xd-qa-name":"First Name",
                }}
                required={true}
                textLabel="First Name"
                type="text"
            />
            <FloatingInput
                classContainer="input"
                classLabel={`label ${!enabled ? 'disabled' : ''}`}
                props={{
                    ...bindInput({
                        value: scopedState(state, ["lastName"]).value,
                        onChange: (value) => {
                            scopedState(state, ["lastName"]).onChange(value);
                            showError.onChange(false);
                        }
                    }),
                    autoComplete: "family-name",
                    className: "value",
                    disabled: !enabled,
                    "xd-qa-type":"textbox",
                    "xd-qa-name":"Last Name",
                }}
                required={true}
                textLabel="Last Name"
                type="text"
            />
            <FloatingInput
                classContainer="input"
                classLabel={`label ${!enabled ? 'disabled' : ''}`}
                props={{
                    ...bindInput({
                        value: scopedState(state, ["email"]).value,
                        onChange: (value) => {
                            showError.onChange(false);
                            scopedState(state, ["email"]).onChange(value);
                        }
                    }),
                    autoComplete: "email",
                    className: "value",
                    disabled: !enabled,
                    "xd-qa-type":"textbox",
                    "xd-qa-name":"Email",
                }}
                required={true}
                textLabel="Email"
                type="text"
            />
            <FloatingInput
                classContainer="input"
                classLabel={`label ${!enabled ? 'disabled' : ''}`}
                props={{
                    ...bindInput({
                        value: scopedState(state, ["phone"]).value,
                        onChange: (value) => {
                            value = formatPhone(value)
                            scopedState(state, ["phone"]).onChange(value);
                            showError.onChange(false);
                        }
                    }),
                    autoComplete: "tel-national",
                    className: "value",
                    disabled: !enabled,
                    "xd-qa-type":"textbox",
                    "xd-qa-name":"Phone",
                }}
                required={true}
                textLabel="Phone"
                type="tel"
            />
            {
                includeAddressFields &&
                <>
                    <FloatingInput
                        classContainer="input"
                        classLabel={`label ${!enabled ? 'disabled' : ''}`}
                        props={{
                            ...bindInput({
                                value: scopedState(state, ["address"]).value,
                                onChange: (value) => {
                                    scopedState(state, ["address"]).onChange(value);
                                    showError.onChange(false);
                                }
                            }),
                            autoComplete: "address-line1",
                            className: "value",
                            disabled: !enabled,
                            "xd-qa-type":"textbox",
                            "xd-qa-name":"Address1",
                        }}
                        required={true}
                        textLabel="Address 1"
                        type="text"
                    />
                    <FloatingInput
                        classContainer="input"
                        classLabel={`label ${!enabled ? 'disabled' : ''}`}
                        props={{
                            ...bindInput({
                                value: scopedState(state, ["address2"]).value,
                                onChange: (value) => {
                                    scopedState(state, ["address2"]).onChange(value);
                                    showError.onChange(false);
                                }
                            }),
                            autoComplete: "address-line2",
                            className: "value",
                            disabled: !enabled,
                            "xd-qa-type":"textbox",
                            "xd-qa-name":"Address2",
                        }}
                        required={false}
                        textLabel="Address 2"
                        type="text"
                    />
                    <FloatingInput
                        classContainer="input"
                        classLabel={`label ${!enabled ? 'disabled' : ''}`}
                        props={{
                            ...bindInput({
                                value: scopedState(state, ["zip"]).value,
                                onChange: (value) => {
                                    scopedState(state, ["zip"]).onChange(value);
                                    showError.onChange(false);
                                }
                            }),
                            autoComplete: "postal-code",
                            className: "value",
                            disabled: !enabled,
                            "xd-qa-type":"textbox",
                            "xd-qa-name":"Zip",
                        }}
                        required={true}
                        textLabel="Zip"
                        type="text"
                    />
                </>
            }
            <label className={`label ${!enabled ? 'disabled' : ''}`}>Message</label>
            <textarea
                {...bindInput({
                    value: scopedState(state, ["description"]).value,
                    onChange: (value) => {
                        scopedState(state, ["description"]).onChange(value);
                        showError.onChange(false);
                    }
                })}
                className={`value ${!enabled ? 'disabled' : ''}`}
                disabled={!enabled}
                xd-qa-type="textbox"
                xd-qa-name="Message"
                style= {{height: "111px"}}
            />
            {showError.value && <div className="error-message">
                {contactFormStateValidatorMessage({state, includeAddressFields})}
            </div>}
        </div>
    )
}

const formatPhone = (value) => {
    // Remove all non-digit characters
    value = value.replace(/\D/g, '');

    // Ensure the value does not exceed 10 digits
    if (value.length > 10) {
        return value;
    }

    // Format the value as a phone number
    if (value.length > 0) {
        value = `(${value}`;
    }
    if (value.length > 4) {
        value = `${value.slice(0, 4)}) ${value.slice(4)}`;
    }
    if (value.length > 9) {
        value = `${value.slice(0, 9)}-${value.slice(9)}`;
    }

    return value;
};

